/**
 * SessionsSubTabs Component
 * Sub-tab navigation within the Sessions tab: Coaching, Booked.
 * Matches the Figma "Elements Subtab / Sessions" pattern with title, divider, sub-tabs, and CTA.
 */

import { Info, Search, X } from "lucide-react"
import { useState } from "react"
import { useNavigate, useSearchParams } from "react-router-dom"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Switch } from "@/components/ui/switch"
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip"
import { useAuthStore } from "@/features/auth/store/authStore"
import { CoachingTabContent } from "@/features/profile/components/CoachingTabContent"
import { useRefreshUserProfile } from "@/features/profile/hooks/useRefreshUserProfile"
import { updateProfile } from "@/features/profile/services/user.service"
import { useTranslation } from "@/language"
import { cn } from "@/lib"
import type { SessionsSubTab } from "../types/my-rituality.types"
import { BookedTabContent } from "./BookedTabContent"

interface SessionsSubTabsProps {
  userId: string
  searchQuery?: string
  coachings?: {
    created: number
    booked: number
    total: number
  }
}

export function SessionsSubTabs({ userId, searchQuery, coachings }: SessionsSubTabsProps) {
  const { t } = useTranslation("myRituality")
  const navigate = useNavigate()
  const [searchParams, setSearchParams] = useSearchParams()
  const currentUser = useAuthStore(state => state.user)
  const isOwner = currentUser?.id === userId
  const { refreshUserProfile } = useRefreshUserProfile()

  const [activeSubTab, setActiveSubTab] = useState<SessionsSubTab>(() => {
    const subTabParam = searchParams.get("subTab")
    return (subTabParam as SessionsSubTab) || "coaching"
  })
  const [showSearch, setShowSearch] = useState(false)
  const [localSearch, setLocalSearch] = useState("")
  const [availableForCoaching, setAvailableForCoaching] = useState(
    () => currentUser?.availableForCoaching ?? false,
  )
  const [isUpdatingCoaching, setIsUpdatingCoaching] = useState(false)

  const coachingCount = coachings?.created ?? 0
  const bookedCount = coachings?.booked ?? 0

  const subTabs: { key: SessionsSubTab; label: string; count: number }[] = [
    { key: "coaching", label: t("sessionsSubTabs.coaching"), count: coachingCount },
    { key: "booked", label: t("sessionsSubTabs.booked"), count: bookedCount },
  ]

  // Update URL when sub-tab changes
  const handleSubTabChange = (value: SessionsSubTab) => {
    setActiveSubTab(value)
    setSearchParams(prev => {
      const newParams = new URLSearchParams(prev)
      newParams.set("subTab", value)
      return newParams
    })
  }

  const handleCoachingToggle = async (value: boolean) => {
    setAvailableForCoaching(value)
    setIsUpdatingCoaching(true)
    try {
      await updateProfile({ available_for_coaching: value })
      await refreshUserProfile()
    } catch {
      setAvailableForCoaching(!value)
    } finally {
      setIsUpdatingCoaching(false)
    }
  }

  return (
    <div className="px-5 md:px-0">
      <div className="flex items-center justify-between">
        <div className="flex md:items-center flex-col w-full md:flex-row gap-5 md:gap-3.75">
          <div className="flex justify-between items-center gap-2">
            <div className="text-[18px] font-semibold leading-7.5 uppercase text-text-primary">
              {t("tabs.sessions")}
            </div>
            <div className="md:hidden flex">
              {showSearch ? (
                <div className="flex items-center gap-2 rounded-sm border border-white/15 bg-white/5 px-3 py-1.5 h-7.5">
                  <Search className="w-4 h-4 text-primary/50 shrink-0" />
                  <Input
                    type="text"
                    placeholder={t("tabs.search")}
                    value={localSearch}
                    onChange={e => setLocalSearch(e.target.value)}
                    className="max-w-48 border-none h-7.5 bg-transparent px-2 text-sm text-primary shadow-none focus-visible:ring-0"
                    autoFocus
                  />
                  <Button
                    variant="ghost"
                    size="icon"
                    className="shrink-0 h-6 w-6 text-primary/50"
                    onClick={() => {
                      setShowSearch(false)
                      setLocalSearch("")
                    }}
                    aria-label={t("tabs.closeSearch")}
                  >
                    <X className="w-4 h-4" />
                  </Button>
                </div>
              ) : (
                <button
                  type="button"
                  className="opacity-50 cursor-pointer h-5"
                  onClick={() => setShowSearch(true)}
                  aria-label={t("tabs.search")}
                >
                  <Search className="w-5 h-5 text-primary" />
                </button>
              )}
            </div>
          </div>

          <div className="w-px h-7.5 bg-text-primary/10 hidden md:block" />

          <div className="flex items-center h-7.5 gap-3.75 md:gap-5 text-sm text-white overflow-x-auto scrollbar-hide">
            {subTabs.map(tab => (
              <button
                key={tab.key}
                type="button"
                onClick={() => handleSubTabChange(tab.key)}
                className={cn(
                  "text-sm cursor-pointer transition-opacity whitespace-nowrap shrink-0",
                  activeSubTab === tab.key ? "font-semibold opacity-100" : "font-normal opacity-70",
                )}
              >
                {tab.label} ({tab.count})
              </button>
            ))}
          </div>
        </div>

        {activeSubTab === "coaching" ? (
          <div className="flex items-center gap-3">
            {isOwner && (
              <Button
                variant="default"
                type="button"
                onClick={() => navigate("/coaching-sessions/create")}
                className="hidden md:block h-[50px] w-[220px] rounded-full border border-text-primary text-text-primary text-[14px] font-semibold uppercase cursor-pointer hover:opacity-90 transition-opacity"
              >
                {t("sessionsSubTabs.addSession")}
              </Button>
            )}
          </div>
        ) : (
          <div>
            <button
              type="button"
              onClick={() => navigate("/connect/experts")}
              className="hidden md:block h-[50px] w-[220px] rounded-full bg-text-primary text-my-rituality-bg text-[14px] font-semibold uppercase cursor-pointer hover:opacity-90 transition-opacity"
            >
              {t("sessionsSubTabs.exploreExperts")}
            </button>
          </div>
        )}
      </div>

      {/* Description and coaching availability - owner only */}
      {isOwner && activeSubTab === "coaching" && (
        <>
          <div className="text-sm leading-6.25 max-w-[622px] mt-5 md:mt-2.5">
            Coaching lets you offer <span className="font-semibold">one-on-one</span> or{" "}
            <span className="font-semibold">private sessions</span>. People can book private
            sessions with you to go deeper, ask personal questions, or receive individual support.
          </div>

          <div className="mt-5 md:mt-7.5 flex items-center gap-2">
            <Switch
              checked={availableForCoaching}
              onCheckedChange={handleCoachingToggle}
              disabled={isUpdatingCoaching}
            />
            <span className="text-sm">Available for coaching</span>
            <Tooltip>
              <TooltipTrigger asChild>
                <Info className="h-4 w-4 cursor-help" />
              </TooltipTrigger>
              <TooltipContent>
                <p>When enabled, others can book coaching sessions with you.</p>
              </TooltipContent>
            </Tooltip>
          </div>
        </>
      )}

      {activeSubTab === "coaching" ? (
        <div className="flex items-center gap-3">
          {isOwner && (
            <Button
              variant="default"
              type="button"
              onClick={() => navigate("/coaching-sessions/create")}
              className="block md:hidden h-[50px] w-full my-7.5 rounded-full border border-text-primary text-text-primary text-[14px] font-semibold uppercase cursor-pointer hover:opacity-90 transition-opacity"
            >
              {t("sessionsSubTabs.addSession")}
            </Button>
          )}
        </div>
      ) : (
        <div>
          <button
            type="button"
            onClick={() => navigate("/connect/experts")}
            className="block md:hidden h-[50px] w-full my-7.5 rounded-full bg-transparent text-primary border border-[#F2F0EB] text-[14px] font-semibold uppercase cursor-pointer hover:opacity-90 transition-opacity"
          >
            {t("sessionsSubTabs.exploreExperts")}
          </button>
        </div>
      )}

      {/* Content */}
      {activeSubTab === "coaching" && (
        <CoachingTabContent
          userId={userId}
          searchQuery={searchQuery}
          className="mt-0"
          showHeader={false}
        />
      )}

      {activeSubTab === "booked" && <BookedTabContent searchQuery={searchQuery} />}
    </div>
  )
}
